import { parseISO } from 'date-fns';
import { isEqual } from 'lodash';
import { Utils } from '../../common/Utils';
import { EmployeeStatusMap, EmployeeTrainingStatus, IntegrationSource } from '../../constants';
import { IChecklists } from '../../interfaces/CheckLists';
import { Answer } from '../../interfaces/Participants';
import { SELECTED_COMPETENCY_TYPE } from '../../interfaces/Practical';
import { User } from '../users/User';
import { SettingsAttribute, SettingsAttributeData } from './SettingsAttribute';

// Employee status type
export interface EmployeeStatus {
  index: number;
  label: string;
}

// Job information type
export interface EmployeeJob {
  jobDefinitionId?: string;
  jobId?: string;
  name?: string;
  manager?: { jobId?: string };
}

// Integration user type
export interface IntegrationUser {
  username?: string;
  isForAllIntegrations?: boolean;
  organizationIntegrationId?: string;
}

// Training type
export interface EmployeeTraining {
  id?: string;
  rowId?: string;
  name?: string;
  completedDate?: Date | string;
  integrationCourseId?: string;
  integration?: { id?: string; name?: string };
  status?: string;
  hasDocuments?: boolean;
  validationRequestState?: number;
  organizationIntegrationIntegrationSource?: IntegrationSource;
  hasActions?: boolean;
  checklists?: IChecklists[];
  isNew?: boolean;
  commentText?: string;
  supportingEvidenceUploadIds?: string[];
  deletedDocumentIds?: string[];
  deletedChecklistAssignmentIds?: string[];
  checklistAnswers?: IChecklists[];
}

// API data for SettingsEmployee
export interface SettingsEmployeeData {
  employeeId?: string;
  firstName?: string;
  lastName?: string;
  middleName?: string;
  preferredName?: string;
  email?: string;
  status?: number;
  role?: string;
  hasJob?: boolean;
  manager?: SettingsEmployee;
  dateOfBirth?: string;
  startDate?: string;
  jobTitle?: string;
  jobDefinitionName?: string;
  locationId?: string;
  groupManagerLevelGroupManagerLevelTypeId?: number;
  groupManagerNotificationGroupManagerNotificationTypeId?: number;
  job?: {
    jobDefinitionId?: string;
    jobId?: string;
    jobDefinitionName?: string;
    manager?: { jobId?: string };
  };
  employeeNumber?: string;
  integrationUsers?: IntegrationUser[];
  isInstructor?: boolean;
  attributes?: SettingsAttribute[];
  employeeAttributes?: SettingsAttributeData[];
  trainings?: EmployeeTraining[];
  employeeTrainings?: Array<{
    id?: string;
    trainingName?: string;
    completionDate?: string;
    integrationCourseId?: string;
    organizationIntegrationId?: string;
    organizationIntegrationName?: string;
    validationRequestState?: number;
    hasDocuments?: boolean;
    organizationIntegrationIntegrationSource?: IntegrationSource;
    employeeTrainingValidationRequest?: { completionDate?: string };
    checklists?: IChecklists[];
  }>;
  numberOfTeamMembers?: number;
  gender?: string;
  fullName?: string;
  isTrainingManager?: boolean;
  trainingManagerConfigurationTypeId?: string;
  trainingManagerConfigurationTypeName?: string;
  isFullAccess?: boolean;
  /** The organization's directory sets this person's job / location; changing it here sets it in Klayo instead. */
  isJobManagedByDirectory?: boolean;
  isLocationManagedByDirectory?: boolean;
  isManagerManagedByDirectory?: boolean;
  /** A group member the directory added (a mapped department). */
  fromDirectory?: boolean;
  /** Fields set in Klayo for this person, which the directory leaves alone until returned. */
  directoryOverrides?: DirectoryFieldOverride[];
  /** False: the organization keeps the fields its directory sets in the directory only - read-only here. */
  canOverrideDirectory?: boolean;
}

/** Mirrors KlayoLib.Model.DirectoryField. */
export enum DirectoryField {
  JobTitle = 0,
  Department = 1,
  Location = 2,
  Manager = 3,
}

/** A field set in Klayo for one person, and what the directory says for it (null: nothing different). */
export interface DirectoryFieldOverride {
  field: DirectoryField;
  directoryValue: string | null;
  createdDate: string;
}

export class SettingsEmployee extends User {
  attributes: SettingsAttribute[];
  integrationUsers: IntegrationUser[];
  integrationUserIds: IntegrationUser[];
  statusLabel: string | null;
  role: string | undefined;
  manager: SettingsEmployee | undefined;
  dateOfBirth: Date | null | undefined;
  startDate: Date | null | undefined;
  locationId: string | undefined;
  groupLevelType: number | undefined;
  groupNotificationType: number | undefined;
  job: EmployeeJob | undefined;
  reportsTo: string | undefined;
  employeeNumber: string | undefined;
  numberOfTeamMembers: number;
  gender: string | undefined;
  trainings: EmployeeTraining[];
  isTrainingManager!: boolean;
  trainingManagerConfigurationTypeId: string | undefined;
  trainingManagerConfigurationTypeName: string | undefined;
  isFullAccess: boolean;
  isJobManagedByDirectory: boolean;
  isLocationManagedByDirectory: boolean;
  isManagerManagedByDirectory: boolean;
  fromDirectory: boolean;
  directoryOverrides: DirectoryFieldOverride[];
  canOverrideDirectory: boolean;

  static status = {
    added: {
      //Added
      index: 0,
      label: 'Added',
    },
    invited: {
      //Invited
      index: 1,
      label: 'Invited',
    },
    active: {
      //Active
      index: 2,
      label: 'Active',
    },
    inactive: {
      //Inactive
      index: 3,
      label: 'Inactive',
    },
  };

  constructor(data?: SettingsEmployeeData) {
    super({ data: data });

    this.attributes = [];
    this.integrationUsers = [];
    this.integrationUserIds = [];
    this.trainings = [];
    this.statusLabel = null;
    this.numberOfTeamMembers = 0;
    this.isFullAccess = data?.isFullAccess ?? true;
    this.isJobManagedByDirectory = !!data?.isJobManagedByDirectory;
    this.isLocationManagedByDirectory = !!data?.isLocationManagedByDirectory;
    this.isManagerManagedByDirectory = !!data?.isManagerManagedByDirectory;
    this.fromDirectory = !!data?.fromDirectory;
    this.directoryOverrides = data?.directoryOverrides ?? [];
    this.canOverrideDirectory = data?.canOverrideDirectory ?? true;

    if (data) {
      this.status =
        data.status !== undefined
          ? Object.values(SettingsEmployee.status)[data.status] || null
          : null;
      this.statusLabel = this.status ? (this.status as EmployeeStatus).label : null;
      this.role = data.role;
      this.hasJob = !!data.hasJob;
      this.manager = data.manager;
      this.dateOfBirth = data.dateOfBirth ? new Date(parseISO(data.dateOfBirth)) : null;
      this.startDate = data.startDate ? new Date(parseISO(data.startDate)) : null;
      this.jobTitle = data.jobTitle ?? data.jobDefinitionName ?? '';
      this.locationId = data.locationId;
      this.groupLevelType = data.groupManagerLevelGroupManagerLevelTypeId;
      this.groupNotificationType = data.groupManagerNotificationGroupManagerNotificationTypeId;
      this.isTrainingManager = data.isTrainingManager || false;
      this.trainingManagerConfigurationTypeId = data.trainingManagerConfigurationTypeId;
      this.trainingManagerConfigurationTypeName = data.trainingManagerConfigurationTypeName;

      if (data.job) {
        this.job = {
          jobDefinitionId: data.job.jobDefinitionId,
          jobId: data.job.jobId,
          name: data.job.jobDefinitionName,
          manager: data.job.manager,
        };

        if (this.job.manager) this.reportsTo = this.job.manager.jobId;
      }
      this.employeeNumber = data.employeeNumber;
      this.integrationUsers = data.integrationUsers || [];
      this.integrationUserIds = data.integrationUsers || [];
      this.isInstructor = data.isInstructor || false;

      if (data.attributes) {
        this.attributes = data.attributes;
      } else if (data.employeeAttributes) {
        data.employeeAttributes.forEach((a) => {
          const attr = new SettingsAttribute(a);
          if (!this.attributes.some((e) => e.rowId === attr.rowId)) this.attributes.push(attr);
          this.attributes = this.attributes.concat(attr.childAttributes);
        });
      }

      if (data.trainings) {
        this.trainings = data.trainings;
      } else if (data.employeeTrainings) {
        this.trainings = data.employeeTrainings.map((training): EmployeeTraining => {
          return {
            completedDate:
              training.validationRequestState === EmployeeTrainingStatus.Approved
                ? training.completionDate
                : training.employeeTrainingValidationRequest?.completionDate,
            name: training.trainingName,
            integrationCourseId: training.integrationCourseId,
            integration: {
              id: training.organizationIntegrationId,
              name: training.organizationIntegrationName,
            },
            status: EmployeeStatusMap.get(training.validationRequestState || 0),
            hasDocuments: training.hasDocuments,
            validationRequestState: training.validationRequestState,
            id: training.id,
            rowId: training.id,
            organizationIntegrationIntegrationSource:
              training.organizationIntegrationIntegrationSource,
            hasActions:
              training.organizationIntegrationIntegrationSource === IntegrationSource.Manual,
            checklists: training.checklists
              ? training.checklists.map((c: IChecklists) => ({
                  ...c,
                  answers: Utils.getAnswersFromChecklist(c),
                }))
              : [],
          };
        });
      }
    } else {
      this.status = SettingsEmployee.status.added;
      this.statusLabel = this.status ? (this.status as EmployeeStatus).label : null;
      this.dateOfBirth = null;
      this.startDate = null;
    }

    this.initAttributesLists();
  }

  isActive() {
    return this.status === SettingsEmployee.status.active;
  }

  isInvited() {
    return this.status === SettingsEmployee.status.invited;
  }

  initAttributesLists(): void {
    // Reserved for future attribute list initialization
  }

  setManager(data: {
    employee?: SettingsEmployeeData;
    numberOfTeamMembers?: number;
    jobDefinitionName?: string;
    jobId?: string;
  }): void {
    const manager = new SettingsEmployee(data.employee);
    manager.numberOfTeamMembers = data.numberOfTeamMembers || 0;
    manager.jobTitle = data.jobDefinitionName ?? '';
    (manager as SettingsEmployee & { jobId?: string }).jobId = data.jobId;
    if (!data.employee) manager.fullName = 'Vacancy';
    this.manager = manager;
  }

  setStatus(status: EmployeeStatus | null): void {
    this.status = status;
    this.statusLabel = this.status ? this.status.label : null;
  }

  getAttribute(id: string): SettingsAttribute | undefined {
    return this.attributes.find((a) => a.attributeId === id && !a.isChild);
  }

  hasManager(): boolean {
    return this.manager !== undefined && this.manager !== null;
  }

  updateAttribute(attribute: SettingsAttribute): void {
    const attributes = [...this.attributes];
    const existingAttr = this.getAttribute(attribute.attributeId!);
    const index = existingAttr ? attributes.indexOf(existingAttr) : -1;
    if (index !== -1) attributes.splice(index, 1, attribute);
    this.attributes = [...attributes];
  }

  isAttributesSame(
    other: SettingsAttribute[],
    find?: (a: SettingsAttribute, o: SettingsAttribute) => boolean
  ): boolean {
    if (!other) return false;
    const otherNoChildren = other.filter((a) => !a.isChild);
    if (other.length !== this.attributes.length) return false;

    for (const o of otherNoChildren) {
      const match = this.attributes.find((a) =>
        find ? find(a, o) : a.attributeId === o.attributeId && !a.isChild
      );

      if (!match || !match.isSame(o)) return false;
    }

    return true;
  }

  isTrainingsSame(
    other: EmployeeTraining[],
    find?: (a: EmployeeTraining, o: EmployeeTraining) => boolean
  ): boolean {
    if (!other || other.length !== this.trainings.length) return false;

    for (const o of other) {
      const match = this.trainings.find((a) => (find ? find(a, o) : a.id === o.id));
      if (!match || !isEqual(match, o)) return false;
    }

    return true;
  }

  toApi(): Record<string, unknown> {
    const payload: Record<string, unknown> = {};

    if (this.employeeId) {
      payload.employeeId = this.employeeId;
    }
    if (this.firstName) {
      payload.firstName = this.firstName;
    }
    if (this.middleName) payload.middleName = this.middleName;
    if (this.preferredName) payload.preferredName = this.preferredName;
    if (this.lastName) payload.lastName = this.lastName;
    if (this.dateOfBirth) payload.dateOfBirth = Utils.toApiDate(this.dateOfBirth);
    if (this.employeeNumber) payload.employeeNumber = this.employeeNumber;
    if (this.gender) payload.gender = this.gender;
    if (this.startDate) payload.startDate = Utils.toApiDate(this.startDate);

    payload.email = this.email;
    payload.isOrganizationAdmin = this.isOrganizationAdmin || false;
    payload.isBilling = this.isBilling || false;

    if (this.locationId) payload.locationId = this.locationId;

    if (this.job) {
      if (this.job.jobDefinitionId) payload.jobDefinitionId = this.job.jobDefinitionId;
      if (this.job.jobId) payload.jobId = this.job.jobId;
    }

    if (this.reportsTo) payload.reportsToId = this.reportsTo;

    // Integration Users
    if (this.integrationUsers && this.integrationUsers.length > 0) {
      payload.integrationUserIds = this.integrationUsers.map((user) => ({
        userName: user.username,
        isForAllIntegrations: user.isForAllIntegrations,
        ...(user.organizationIntegrationId && {
          integrationId: user.organizationIntegrationId,
        }),
      }));
    }

    // Employee Attributes
    const filteredAttributes = this.attributes.filter((a) => a.isChild !== true);

    if (filteredAttributes.length > 0) {
      payload.employeeAttributes = filteredAttributes.map((a) => {
        const attr: Record<string, unknown> = {
          attributeDefinitionId: a.attributeDefinitionId,
        };

        if (a.comment) attr.commentText = a.comment;
        if (a.validationRequestState) attr.state = a.validationRequestState.index;
        if (a.pendingProficiency) {
          attr.proficiencyValue = Number.isInteger(a.pendingProficiency) ? a.pendingProficiency : 0;
        }
        if (a.pendingExpiryDate) {
          attr.expiryDate = Utils.toApiDate(a.pendingExpiryDate as Date);
        }

        if (a.supportingEvidenceUploadIds) {
          attr.supportingEvidenceUploadIds = a.supportingEvidenceUploadIds;
        }

        if (a.documents) {
          const deletedDocs = a.documents.list.filter(
            (d: { pendingDelete?: boolean }) => d.pendingDelete
          );
          if (deletedDocs.length > 0) {
            attr.deletedEvidences = deletedDocs.map((d: { documentId?: string }) => d.documentId);
          }
        }

        if (a.checklistAnswers) {
          attr.checklistAnswers = a.checklistAnswers.map((c) => ({
            checklistId: c.id,
            appliesTo: SELECTED_COMPETENCY_TYPE.SELECTED,
            answers: (c.answers || []).map((answer: Answer) => ({
              questionId: answer?.questionId,
              textAnswer: answer?.textAnswer,
              dateAnswer: answer?.dateAnswer ? Utils.toDtoDate(answer?.dateAnswer) : null,
              selectedOptionIds: answer?.selectedOptionIds || [],
            })),
          }));
        }

        if (a.deletedChecklistAssignmentIds) {
          attr.deletedChecklistAssignmentIds = a.deletedChecklistAssignmentIds;
        }

        return attr;
      });
    }

    // Employee Trainings
    const manualTrainings = this.trainings.filter(
      (training) => training.organizationIntegrationIntegrationSource === IntegrationSource.Manual
    );

    if (manualTrainings.length > 0) {
      payload.employeeTrainings = manualTrainings.map((training) => {
        const trainingObj: Record<string, unknown> = {
          integrationCourseId: training.integrationCourseId,
        };

        if (training.id && !training.isNew) {
          trainingObj.employeeTrainingId = training.id;
        }
        if (training.commentText) trainingObj.commentText = training.commentText;
        if (training.validationRequestState) {
          trainingObj.status = training.validationRequestState;
        }
        if (training.completedDate) {
          trainingObj.completionDate = training.completedDate;
        }

        if (training.supportingEvidenceUploadIds) {
          trainingObj.supportingEvidenceUploadIds = training.supportingEvidenceUploadIds;
        }

        if (training.deletedDocumentIds && training.deletedDocumentIds.length > 0) {
          trainingObj.deletedEvidences = training.deletedDocumentIds;
        }

        if (
          training.deletedChecklistAssignmentIds &&
          training.deletedChecklistAssignmentIds.length > 0
        ) {
          trainingObj.deletedChecklistAssignmentIds = training.deletedChecklistAssignmentIds;
        }

        if (training.checklistAnswers) {
          trainingObj.checklistAnswers = training.checklistAnswers.map((c) => ({
            checklistId: c.id,
            appliesTo: SELECTED_COMPETENCY_TYPE.SELECTED,
            answers: (c.answers || []).map((answer: Answer) => ({
              questionId: answer?.questionId,
              textAnswer: answer?.textAnswer,
              dateAnswer: answer?.dateAnswer ? Utils.toDtoDate(answer?.dateAnswer) : null,
              selectedOptionIds: answer?.selectedOptionIds || [],
            })),
          }));
        }

        return trainingObj;
      });
    }

    return payload;
  }

  toApiDto(): {
    employeeId: string;
    firstName: string;
    middleName: string | undefined;
    preferredName: string | undefined;
    lastName: string;
    dateOfBirth: string | null;
    employeeNumber: string | undefined;
    gender: string | undefined;
    startDate: string | null;
    email: string;
    locationId: string | undefined;
    isOrganizationAdmin: boolean;
    isBilling: boolean;
    jobDefinitionId: string | null | undefined;
    jobId: string | null | undefined;
    reportsToId: string | undefined;
    employeeAttributes: Array<{
      attributeDefinitionId: string | undefined;
      proficiencyValue: number;
      comment: string | undefined;
      state: number | null;
      expiryDate: null;
    }>;
  } {
    const attrs: Array<{
      attributeDefinitionId: string | undefined;
      proficiencyValue: number;
      comment: string | undefined;
      state: number | null;
      expiryDate: null;
    }> = [];

    this.attributes
      .filter((a) => a.isChild !== true)
      .forEach((a) => {
        attrs.push({
          attributeDefinitionId: a.attributeDefinitionId,
          proficiencyValue: a.proficiencyMinimum || 0,
          comment: a.comment,
          state: a.validationRequestState ? a.validationRequestState.index : null,
          expiryDate: null,
        });
      });

    return {
      employeeId: this.employeeId,
      firstName: this.firstName,
      middleName: this.middleName,
      preferredName: this.preferredName,
      lastName: this.lastName,
      dateOfBirth: Utils.toApiDate(this.dateOfBirth),
      employeeNumber: this.employeeNumber,
      gender: this.gender,
      startDate: Utils.toApiDate(this.startDate),
      email: this.email,
      locationId: this.locationId,
      isOrganizationAdmin: this.isOrganizationAdmin || false,
      isBilling: this.isBilling || false,
      jobDefinitionId: this.job ? this.job.jobDefinitionId : null,
      jobId: this.job ? this.job.jobId : null,
      reportsToId: this.reportsTo,
      employeeAttributes: attrs,
    };
  }
}
